import React from 'react';

import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';

import type {SelfScheduledStateInfo} from './getSelfScheduledState';

/**
 * Format a future ISO-8601 timestamp as a friendly relative time with an absolute
 * time in parentheses. Examples: "in 5 minutes (3:45 PM)", "any moment now (3:44 PM)",
 * "in about 2 hours (Mar 20 6:00 AM)". Only includes the date when it differs from today.
 */
function formatScheduledTime(isoString: string): string {
  const wake = new Date(isoString);
  const now = new Date();
  const diffMs = wake.getTime() - now.getTime();
  const diffSeconds = Math.round(diffMs / 1000);

  let relative: string;
  if (diffSeconds <= 0) {
    relative = 'any moment now';
  } else if (diffSeconds < 60) {
    relative = `in ${diffSeconds}s`;
  } else if (diffSeconds < 3600) {
    const mins = Math.round(diffSeconds / 60);
    relative = `in ${mins} minute${mins === 1 ? '' : 's'}`;
  } else {
    const hours = Math.round(diffSeconds / 3600);
    relative = `in about ${hours} hour${hours === 1 ? '' : 's'}`;
  }

  const isToday =
    wake.getFullYear() === now.getFullYear() &&
    wake.getMonth() === now.getMonth() &&
    wake.getDate() === now.getDate();

  const timeStr = wake.toLocaleTimeString([], {hour: 'numeric', minute: '2-digit'});
  const absolute = isToday
    ? timeStr
    : `${wake.toLocaleDateString([], {month: 'short', day: 'numeric'})} ${timeStr}`;

  return `${relative} (${absolute})`;
}

interface SelfScheduledBannerProps {
  selfScheduledState: SelfScheduledStateInfo;
}

export default function SelfScheduledBanner({selfScheduledState}: SelfScheduledBannerProps): JSX.Element {
  const formattedTime = formatScheduledTime(selfScheduledState.wakeAt);
  return (
    <InlineBanner
      bannerType={InlineBannerType.INFO}
      text={`This chat will resume ${formattedTime}. Send a message to run earlier or cancel. Reason: ${selfScheduledState.reason}`}
    />
  );
}
